import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import {
  DEFAULT_UNSAVED_CHANGES_MESSAGE_LINES,
  UnsavedChangesDialogComponent,
  UnsavedChangesTrackerService,
} from '@spri/components/dialogs';
import { FilterChipDropdownComponent } from '@spri/components/filter-chip-dropdown';
import { SpriLoadingBarComponent } from '@spri/components/loading-bar';
import type { SpriNavigationItem } from '@spri/components/navigation/navigation.types';
import { type FilterGroup, type FilterKey } from '@spri/components/search-text/search-text.component';
import { SpriMediaWatcherService } from '@spri/services/media-watcher';
import { SPRI_VERSION } from '@spri/version';
import { IdleTimeoutService } from 'app/core/auth/idle-timeout.service';
import { HasPermissionDirective } from 'app/core/auth/roles/directives/has-permission.directive';
import { RolesService } from 'app/core/auth/roles/roles.service';
import { UserRole } from 'app/core/auth/roles/user-role.enum';
import type { HeaderConfig } from 'app/core/header/header.service';
import { HeaderService } from 'app/core/header/header.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import type { Navigation } from 'app/core/navigation/navigation.types';
import { TenantContextService } from 'app/core/services/tenant-context.util';
import { SidenavStore } from 'app/core/stores/sidenav.store';
import { UserComponent } from 'app/layout/common/user/user.component';
import { FilterOptionsScheduleService } from 'app/modules/admin/patient-surveys/shared/services/filter-options.service';
import type { AppointmentFilterOption } from 'app/modules/schedule/types';
import { filter, map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'spri-spri-layout',
  templateUrl: './spri.component.html',
  styleUrls: ['./spri.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('textFade', [
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'translateX(0)',
          maxWidth: '200px',
        })
      ),
      state(
        'hidden',
        style({
          opacity: 0,
          transform: 'translateX(-20px)',
          maxWidth: '0px',
        })
      ),
      transition('visible <=> hidden', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')]),
    ]),
  ],
  imports: [
    CommonModule,
    NgOptimizedImage,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    FilterChipDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    RouterOutlet,
    RouterModule,
    SpriLoadingBarComponent,
    UserComponent,
    HasPermissionDirective,
  ],
})
export class SpriLayoutComponent implements AfterViewInit, OnInit, OnDestroy {
  protected readonly tenantContextService = inject(TenantContextService);
  public readonly appVersion = SPRI_VERSION;
  public isScreenSmall: boolean;
  public navigation: Navigation;
  public currentPageTitle = '';
  public headerConfig: HeaderConfig | undefined;
  public availableRoles: UserRole[] = [];
  private readonly unsubscribeAll = new Subject<void>();
  private isLandingPage = false;

  // Search panel state
  public showFilterPanel = false;
  public searchForm!: FormGroup;
  public selectedFilters: Record<string, string[]> = {};
  public menuSearchText: Record<string, string> = {};
  public hoveredFilterKey: string | null = null;
  public openFilterKey: string | null = null;
  private readonly fb = inject(FormBuilder);

  private readonly filterChipRefs = viewChildren(FilterChipDropdownComponent);

  /**
   * Constructor
   */
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navigationService = inject(NavigationService);
  private readonly spriMediaWatcherService = inject(SpriMediaWatcherService);
  private readonly cdr = inject(ChangeDetectorRef);
  protected readonly sidenavStore = inject(SidenavStore);
  private readonly rolesService = inject(RolesService);
  private readonly filterOptions = inject(FilterOptionsScheduleService);
  // Touch to initialize idle timeout binding
  private readonly idle = inject(IdleTimeoutService);
  private readonly headerService = inject(HeaderService);
  private readonly matDialog = inject(MatDialog);
  protected readonly unsavedChangesTracker = inject(UnsavedChangesTrackerService);

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  public ngAfterViewInit(): void {
    document.addEventListener('click', this.documentClickHandler, true);
  }

  public async ngOnInit(): Promise<void> {
    // Initialize search form
    this.searchForm = this.fb.group({
      query: [''],
    });

    // Subscribe to header config changes for header search
    this.headerService.headerConfigObj.pipe(takeUntil(this.unsubscribeAll)).subscribe((config: HeaderConfig) => {
      this.headerConfig = config;
      if (config.title) {
        this.currentPageTitle = config.title;
      }
      // Restore search panel state when a page provides initial values (e.g. from session storage)
      if (config.initialSearchState) {
        this.searchForm.patchValue({ query: config.initialSearchState.text });
        this.selectedFilters = { ...config.initialSearchState.selected };
      }
      this.cdr.detectChanges();
    });
    this.availableRoles = this.rolesService.getAvailableRoles();
    // Try to get navigation from resolver data first (available immediately on page refresh)
    let route = this.activatedRoute;
    while (route.parent) {
      route = route.parent;
    }
    const resolverData = route.snapshot.data;
    if (resolverData && resolverData['initialData']) {
      const navigation = resolverData['initialData'] as Navigation;
      this.navigation = navigation;
      this.addClickHandlersToNavigationItems();
      this.openModuleForActiveRoute();
      this.cdr.detectChanges();
    }

    // Load navigation data (will update if not already set from resolver)
    this.navigationService
      .get()
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        // Assign raw navigation; UI visibility handled by permission directives
        this.navigation = navigation;
        this.addClickHandlersToNavigationItems();
        // Auto-open module if current route belongs to it
        this.openModuleForActiveRoute();
        // Trigger change detection to ensure navigation renders
        this.cdr.detectChanges();
      });

    // Subscribe to media changes
    this.spriMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        const wasScreenSmall = this.isScreenSmall;
        this.isScreenSmall = !matchingAliases.includes('md');

        // Auto-collapse navigation on mobile screens by default
        // Use collapseWithoutPersist to avoid overwriting desktop preference
        if (this.isScreenSmall) {
          this.sidenavStore.collapseWithoutPersist();
        } else if (wasScreenSmall !== undefined && wasScreenSmall) {
          // Transitioning from small to large screen - restore persisted state
          try {
            const persistedCollapsed = localStorage.getItem('spri_sidenav_collapsed') === 'true';
            if (persistedCollapsed) {
              this.sidenavStore.collapse();
            } else {
              this.sidenavStore.expand();
            }
          } catch {
            // If localStorage access fails, default to expanded
            this.sidenavStore.expand();
          }
        }
        // On initial load for desktop, the state is already loaded from localStorage
        // and will be respected automatically
      });

    // Set initial page title based on current URL
    this.setInitialPageTitle();

    // Set initial landing page flag
    this.updateLandingPageFlag();

    // Subscribe to route changes to update page title
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let currentRoute = this.activatedRoute;
          while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
          }
          return currentRoute;
        }),
        takeUntil(this.unsubscribeAll)
      )
      .subscribe((activeRoute) => {
        // Get the page title from route data or derive from URL
        const routeData = activeRoute.snapshot.data;
        const url = activeRoute.snapshot.url.join('/');

        // Only update title from route if header config doesn't have a title
        // This allows components to override the title via header service
        if (!this.headerConfig?.title) {
          if (routeData && routeData.title) {
            this.currentPageTitle = routeData.title;
          } else {
            // Derive title from URL or navigation
            const derivedTitle = this.getPageTitleFromUrl(url);
            this.currentPageTitle = derivedTitle;
          }
        }

        // Reset search UI unless the destination page restores state (e.g. session storage).
        // Calling handleReset() after initialSearchState would wipe filters the page just restored.
        if (this.headerService.getCurrentHeaderConfig().initialSearchState) {
          this.closeSearchPanel();
        } else {
          this.onSearchReset();
        }

        // Update landing page flag
        this.updateLandingPageFlag();

        // Check if current route has hideFilter flag
        if (routeData?.hideFilter) {
          // Hide the search/filter component for routes with hideFilter flag
          this.headerService.updateHeaderConfig({
            showSearch: false,
            searchFilters: [],
          } as unknown as Record<string, unknown>);
        } else {
          // Show the search/filter component and initialize filters (skip for reception role)
          this.headerService.updateHeaderConfig({
            showSearch: true,
          });
          if (this.rolesService.getCurrentRole() !== UserRole.RECEPTION) {
            this.initSearchFilters();
          }
        }

        this.cdr.markForCheck();
      });

    // Check initial route on page load
    this.checkAndUpdateHeaderForCurrentRoute();
  }

  public ngOnDestroy(): void {
    document.removeEventListener('click', this.documentClickHandler, true);
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();
  }

  /** Capture-phase handler: close dropdown when click is outside the open dropdown */
  private readonly documentClickHandler = (event: MouseEvent): void => {
    if (!this.showFilterPanel || !this.openFilterKey) {
      return;
    }

    const openChip = this.filterChipRefs()?.find((c) => c.filterGroup().key === this.openFilterKey);
    if (!openChip) {
      return;
    }

    const path = event.composedPath();
    const triggerEl = openChip.triggerButton()?.nativeElement;
    const dropdownEl = openChip.dropdownPanel()?.nativeElement;
    const clickedInsideDropdown = (triggerEl && path.includes(triggerEl)) || (dropdownEl && path.includes(dropdownEl));

    if (!clickedInsideDropdown) {
      // Defer to next tick so update runs after any other click handlers
      setTimeout(() => {
        this.openFilterKey = null;
        this.cdr.markForCheck();
      }, 0);
    }
  };

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  public toggleNavigation(): void {
    // Toggle the collapsed state using the store
    this.sidenavStore.toggleCollapse();
  }

  public onLogoClick(): void {
    this.router.navigate(['/home']);
  }

  public onItemClick(item: {
    id?: string;
    title?: string;
    link?: string;
    externalLink?: boolean;
    children?: unknown[];
    function?: (i: unknown) => void;
  }): void {
    if (item.children) {
      this.sidenavStore.toggleItem(item.id || item.title || '');
    } else if (item.link) {
      if (item.externalLink) {
        if (this.unsavedChangesTracker.hasUnsavedChanges()) {
          this.showUnsavedChangesAndOpen(item.link);
        } else {
          window.open(item.link, '_blank');
        }
      } else {
        /**
         * Internal links are already handled by [routerLink] in the template.
         * Avoid issuing a second imperative navigate() from the same click because
         * duplicate navigations can race when guards/token refresh run.
         */
        return;
      }
    } else if (item.function) {
      item.function(item);
    }
  }

  private showUnsavedChangesAndOpen(url: string, target = '_blank'): void {
    const ref = this.matDialog.open(UnsavedChangesDialogComponent, {
      width: '400px',
      disableClose: true,
      panelClass: 'unsaved-changes-dialog-panel',
      data: {
        variant: 'unsaved' as const,
        arrowPosition: 'none' as const,
        title: 'UNSAVED CHANGES',
        messageLines: [...DEFAULT_UNSAVED_CHANGES_MESSAGE_LINES],
        primaryLabel: 'Yes',
        secondaryLabel: 'No',
        primaryButtonType: 'save' as const,
        secondaryButtonType: 'discard' as const,
      },
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((result: string | undefined) => {
        if (result === 'discard') {
          window.open(url, target);
        }
        if (result === 'save') {
          this.unsavedChangesTracker.getActiveChecker()?.onUnsavedDeactivateSave?.();
        }
      });
  }

  public onLeafNavigate(): void {
    // Keep collapsed/expanded state; optional behavior could close submenus
  }

  /**
   * Intercepts clicks on external links (e.g. Form Builder, Survey Builder).
   * When the current page has unsaved changes, prevents the navigation and
   * shows the same "Unsaved Changes" dialog used by the Back-button flow.
   */
  public onExternalLinkClick(event: MouseEvent, child: { link?: string; target?: string }): void {
    if (!this.unsavedChangesTracker.hasUnsavedChanges()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.showUnsavedChangesAndOpen(child.link || '#', child.target || '_blank');
  }

  public hasActiveChild(item: { children?: SpriNavigationItem[] }): boolean {
    if (!item.children || item.children.length === 0) {
      return false;
    }

    return item.children.some((child) => {
      if (child.link && this.router.isActive(child.link, false)) {
        return true;
      }
      // Recursively check nested children
      if (child.children && this.hasActiveChild(child)) {
        return true;
      }
      return false;
    });
  }

  private async initSearchFilters(): Promise<void> {
    // Only load default (Patient-svc) filters when no component has claimed filter ownership.
    // If searchFilters is defined (including []), the component owns filters; don't override.
    if (this.headerService.getCurrentHeaderConfig().searchFilters !== undefined) {
      return;
    }

    const filterOptionsResponse = await this.filterOptions.getFilterOptionsPromise();

    // Re-check after the async call: a page component (e.g. ScheduleComponent) may have
    // claimed ownership of searchFilters while we were awaiting the API response.
    // Without this second guard the layout would override the component's correctly-keyed
    // filters with mismatched keys (e.g. "types" instead of "type") causing selected
    // filter values to never reach the backend query.
    if (this.headerService.getCurrentHeaderConfig().searchFilters !== undefined) {
      return;
    }

    const filterOptions: FilterGroup[] = Object.entries(filterOptionsResponse).map(
      ([key, value]: [FilterKey, AppointmentFilterOption[]]) => ({
        key: key as FilterKey,
        label: key.toUpperCase(),
        options: value.map((item) => ({ label: item.name, value: item.code })),
      })
    );

    this.headerService.updateHeaderConfig({
      searchFilters: filterOptions,
    } as unknown as Record<string, unknown>);
  }

  private checkAndUpdateHeaderForCurrentRoute(): void {
    // Get current route
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const routeData = route.snapshot.data;

    // Check if current route has hideFilter flag
    if (routeData?.hideFilter) {
      // Hide the search/filter component for routes with hideFilter flag
      this.headerService.updateHeaderConfig({
        showSearch: false,
        searchFilters: [],
      } as unknown as Record<string, unknown>);
    } else {
      // Show the search/filter component and initialize filters (skip for reception role)
      this.headerService.updateHeaderConfig({
        showSearch: true,
      });
      if (this.rolesService.getCurrentRole() !== UserRole.RECEPTION) {
        this.initSearchFilters();
      }
    }

    this.cdr.markForCheck();
  }

  /**
   * Add click handlers to navigation items
   */
  private addClickHandlersToNavigationItems(): void {
    if (!this.navigation?.default) {
      return;
    }

    this.navigation.default.forEach((item) => {
      if (item.type === 'collapsable' && item.children && item.children.length > 0) {
        // Store original function
        const originalFunction = item.function;

        // Override the function to include our auto-expand logic
        item.function = (clickedItem: SpriNavigationItem): void => {
          const typed = clickedItem as { id?: string; title?: string };
          // Use the store to handle the toggle logic
          this.sidenavStore.toggleItem(typed.id || typed.title || '');

          // Call the original function if it exists
          if (originalFunction) {
            originalFunction(clickedItem as SpriNavigationItem);
          }
        };
      }
    });
  }
  private openModuleForActiveRoute(): void {
    if (!this.navigation?.default) {
      return;
    }
    const url = this.router.url;
    const match = this.navigation.default.find((item) => {
      if (item.type === 'collapsable' && Array.isArray(item.children)) {
        return item.children.some((child) => !!child.link && url.startsWith(child.link as string));
      }
      return !!item.link && url.startsWith(item.link as string);
    });
    if (match?.id) {
      this.sidenavStore.expandAndOpen(match.id);
    }
  }

  public onNavigationItemClick(): void {
    // This method is now handled directly in addClickHandlersToNavigationItems
    // Keeping it for backward compatibility but it's not used anymore
  }

  public handleSearch(event: { text: string; selected: Record<string, string[]> }): void {
    const cb = this.headerService.getCurrentHeaderConfig().onSearch as
      | ((payload: { text: string; selected: Record<string, string[]> } | string) => void)
      | undefined;
    if (typeof cb === 'function') {
      cb(event);
    }
  }

  public handleReset(): void {
    const cb = this.headerService.getCurrentHeaderConfig().onSearch as
      | ((payload: { text: string; selected: Record<string, string[]> } | string) => void)
      | undefined;
    if (typeof cb === 'function') {
      cb({ text: '', selected: {} });
    }
  }

  public onSearchInputEnter(event: KeyboardEvent): void {
    event.preventDefault(); // Prevent form submission or other default behavior
    event.stopPropagation(); // Stop event from bubbling up to parent handlers
    // Call the exact same function as the search button - this ensures identical behavior
    this.performSearch(this.searchForm.value.query || '');
  }

  public onSearchInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    // Update the form control to sync both inputs
    this.searchForm.patchValue({ query: value }, { emitEvent: true });
    this.cdr.markForCheck();
  }

  /** Clear search input only (panel stays open). */
  public clearSearchInput(): void {
    this.searchForm.patchValue({ query: '' }, { emitEvent: true });
    this.cdr.markForCheck();
  }

  /**
   * Opens the panel on Enter when focus is on the container (a11y).
   * Ignores bubbled Enter from the inner input so performSearch can close
   * the panel without openSearchPanel running afterward.
   */
  public onSearchContainerEnter(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    this.openSearchPanel();
  }

  public onSearchInputClick(): void {
    this.openSearchPanel();
  }

  public openSearchPanel(): void {
    this.showFilterPanel = true;
    document.body.classList.add('spri-modal-open');
    this.cdr.markForCheck();
  }

  public closeSearchPanel(): void {
    this.showFilterPanel = false;
    document.body.classList.remove('spri-modal-open');
    this.cdr.markForCheck();
  }

  public performSearch(text: string): void {
    this.handleSearch({ text, selected: this.selectedFilters });
    this.closeSearchPanel();
  }

  public onSearchReset(): void {
    this.selectedFilters = {};
    this.searchForm.patchValue({ query: '' });
    this.handleReset();
    this.closeSearchPanel();
  }

  public isFilterChecked(key: string, value: string): boolean {
    return this.selectedFilters[key]?.includes(value) ?? false;
  }

  public toggleFilterOption(key: string, value: string): void {
    const arr = (this.selectedFilters[key] ??= []);
    const i = arr.indexOf(value);
    if (i >= 0) {
      arr.splice(i, 1);
    } else {
      arr.push(value);
    }
    this.selectedFilters = { ...this.selectedFilters };
    this.cdr.markForCheck();
  }

  public getFilterCount(key: string): number {
    return this.selectedFilters[key]?.length ?? 0;
  }

  public getFilterLabel(group: FilterGroup): string {
    const count = this.getFilterCount(group.key);
    return count ? `${group.label} (${count})` : group.label;
  }

  public clearFilterGroup(key: string, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.selectedFilters[key] = [];
    this.selectedFilters = { ...this.selectedFilters };
    this.cdr.markForCheck();
  }

  public onFilterChipEnter(key: string): void {
    this.hoveredFilterKey = key;
  }

  public onFilterChipLeave(): void {
    this.hoveredFilterKey = null;
  }

  public onFilterToggle(key: string): void {
    this.openFilterKey = this.openFilterKey === key ? null : key;
    this.cdr.markForCheck();
  }

  public getFilteredOptions(group: FilterGroup): { label: string; value: string }[] {
    const q = (this.menuSearchText[group.key] || '').toLowerCase();
    if (!q) {
      return group.options;
    }
    return group.options.filter((o) => o.label.toLowerCase().includes(q));
  }

  /** Called when selection changes inside a filter chip dropdown; updates selectedFilters so SEARCH applies them. */
  public onFilterSelectionChange(key: string, values: string[]): void {
    this.selectedFilters = { ...this.selectedFilters, [key]: values };
    this.cdr.markForCheck();
  }

  /** Close dropdown when mousedown outside it (mousedown fires before click, ensures we receive the event) */
  public onFilterPanelMousedown(event: MouseEvent): void {
    event.stopPropagation();

    const chips = this.filterChipRefs();
    if (!this.openFilterKey || !chips?.length) {
      return;
    }

    const openChip = chips.find((c) => c.filterGroup().key === this.openFilterKey);
    if (!openChip) {
      return;
    }

    const path = event.composedPath();
    const triggerEl = openChip.triggerButton()?.nativeElement;
    const panelEl = openChip.dropdownPanel()?.nativeElement;
    const clickedInside = (triggerEl && path.includes(triggerEl)) || (panelEl && path.includes(panelEl));

    if (!clickedInside) {
      this.openFilterKey = null;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  public onEscKey(): void {
    if (this.showFilterPanel) {
      this.closeSearchPanel();
    }
  }

  /**
   * Check if the current user can access at least one child of a collapsable item.
   */
  public hasAnyChildPermission(item: SpriNavigationItem): boolean {
    if (!Array.isArray(item.children)) {
      return false;
    }
    return item.children.some((child: SpriNavigationItem) => this.rolesService.canAccess(`nav:${item.id}/${child.id}`));
  }

  /**
   * Proxy to RolesService for template usage.
   */
  public canAccess(permission: string): boolean {
    return this.rolesService.canAccess(permission);
  }

  private setInitialPageTitle(): void {
    const currentUrl = this.router.url;

    if (currentUrl && currentUrl !== '/') {
      this.currentPageTitle = this.getPageTitleFromUrl(currentUrl);
      this.cdr.markForCheck();
    } else {
      this.currentPageTitle = 'Home';
      this.cdr.markForCheck();
    }
  }

  private getPageTitleFromUrl(url: string): string {
    if (!url || url === '/') {
      return 'Home';
    }

    // Map URL segments to readable titles
    const urlMappings: Record<string, string> = {
      schedule: 'Schedule',
      reception: 'Reception',
      'clinical-data': 'Clinical Data',
      'surgery-form': 'Surgery Form',
      'exam-form': 'Exam Form',
      'patient-surveys': 'Automated Surveys',
      manual: 'Manual',
      automated: 'System Emails',
      reports: 'Reports',
      home: 'Home',
    };

    // Get the first segment of the URL
    const firstSegment = url.split('/')[1];

    // If no first segment exists, return empty string
    if (!firstSegment) {
      return '';
    }

    const mappedTitle = urlMappings[firstSegment];
    if (mappedTitle) {
      return mappedTitle;
    }

    try {
      const fallbackTitle = firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1);
      return fallbackTitle;
    } catch (error) {
      console.error('Error getting page title from URL:', error);
      return '';
    }
  }

  /**
   * Update the landing page flag based on current route
   */
  private updateLandingPageFlag(): void {
    const url = this.router.url;
    this.isLandingPage = url === '/home' || url === '/' || url.startsWith('/home');
  }
}
